import type { AdminGymDeletionRequest } from '@steado/admin-contract/gyms';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Panel } from '@/components/panel/panels';
import { BadgeCell, TextCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDate, formatRelative } from '@/lib/format';

import { GYM_DELETIONS_KEY, markGymDeletionDone, openGymDeletionsQueryOptions } from './gym-deletions-query';

const DAY_MS = 86_400_000;
/** What the app promises: deleted within 30 days of asking. */
const DUE_DAYS = 30;

function dueOn(request: AdminGymDeletionRequest): string {
  return formatDate(new Date(Date.parse(request.requestedAt) + DUE_DAYS * DAY_MS).toISOString());
}

/**
 * Trainers and owners who asked, from the Steado for Gyms app, for their
 * account to be deleted. Drawn only while one is open: delete the account by
 * hand, then mark the request done. Erasing a user marks theirs done on its own.
 */
export function GymDeletionRequestsPanel() {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const open = useQuery(openGymDeletionsQueryOptions(api));
  const done = useMutation({
    mutationFn: (id: string) => markGymDeletionDone(api, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: GYM_DELETIONS_KEY });
    },
    retry: false,
  });

  const requests = open.data ?? [];
  if (requests.length === 0) return null;

  return (
    <Panel
      title={`Account deletion requests (${String(requests.length)})`}
      description="Asked for in the Steado for Gyms app. Delete the account within 30 days, then mark the request done. A trainer's account is their Steado account."
      bleed
    >
      {done.isError ? (
        <Alert role="alert" tone="err" className="mx-[18px] mb-3">
          <AlertDescription>{done.error.message}</AlertDescription>
        </Alert>
      ) : null}
      <ul>
        {requests.map((request) => (
          <li
            key={request.id}
            className="grid grid-cols-[1.4fr_1.4fr_1fr_auto] items-center gap-3 border-t border-border px-[18px] py-2.5"
          >
            <TextCell
              value={request.person?.name ?? 'Account already deleted'}
              sub={request.person?.contact ?? null}
            />
            <span className="flex min-w-0 flex-col gap-1">
              <BadgeCell label={request.kind === 'user' ? 'trainer' : 'gym owner'} tone="mute" />
              <span className="truncate text-xs text-muted-foreground">
                {request.gyms.map((gym) => gym.name).join(', ') || 'No gym'}
                {request.reason === null ? '' : ` · “${request.reason}”`}
              </span>
            </span>
            <TextCell value={formatRelative(request.requestedAt)} sub={`due ${dueOn(request)}`} />
            {can.mutate ? (
              <Button
                variant="outline"
                size="xs"
                disabled={done.isPending}
                onClick={() => {
                  done.mutate(request.id);
                }}
              >
                Mark done
              </Button>
            ) : (
              <span />
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
